import z from "zod";

import { type ErrorDetails, RemiHttpCode } from "./remi-app";

const ensiteIntegrationErrorTypeValues = [
  "TEST_ERROR",
  "WEBHOOK_HEADER_MISSING",
  "WEBHOOK_SIGNATURE_INVALID",
  "WEBHOOK_TIMESTAMP_STALE",
  "MISSING_GEOCODED_COORDINATES",
  "MISSING_TIMEZONE",
  "MISSING_INSTALL_DATE",
  "SLOT_LOOKUP_FAILED",
  "NO_BOOKABLE_SLOT",
  "NO_SLOTS_FOR_DATE",
  "DUPLICATE_SUBMISSION",
  "SLOT_PREFILLED",
  "AUTHENTICATION_FAILED",
  "SERVER_UNAVAILABLE",
  "SLOT_CHECK_TIMED_OUT",
  "ZIP_EXTRACTION_REFUSED",
] as const;
const ensiteIntegrationErrorTypeSchema = z.enum(ensiteIntegrationErrorTypeValues);
const EnsiteIntegrationErrorType = ensiteIntegrationErrorTypeSchema.enum;
type EnsiteIntegrationErrorType = z.infer<typeof ensiteIntegrationErrorTypeSchema>;

export type EnsiteIntegrationErrors =
  | {
      errorType: typeof EnsiteIntegrationErrorType.ZIP_EXTRACTION_REFUSED;
      data: { archivePath: string; limit: string; observed: number; allowed: number };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.TEST_ERROR;
      data: undefined;
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.WEBHOOK_HEADER_MISSING;
      data: { header: string };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.WEBHOOK_SIGNATURE_INVALID;
      data: undefined;
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.WEBHOOK_TIMESTAMP_STALE;
      data: undefined;
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.MISSING_GEOCODED_COORDINATES;
      data: { remiProjectId: number };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.MISSING_TIMEZONE;
      data: { remiProjectId: number };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.MISSING_INSTALL_DATE;
      data: { remiProjectId: number };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.SLOT_LOOKUP_FAILED;
      data: { remiProjectId: number };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.NO_BOOKABLE_SLOT;
      data: { remiProjectId: number; installDateKey: string | null; selectedDayCount: number };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.NO_SLOTS_FOR_DATE;
      data: { remiProjectId: number; date: string };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.DUPLICATE_SUBMISSION;
      data: { error: string; msg: string };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.SLOT_PREFILLED;
      data: undefined;
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.AUTHENTICATION_FAILED;
      data: { message: string };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.SERVER_UNAVAILABLE;
      data: { message: string };
    }
  | {
      errorType: typeof EnsiteIntegrationErrorType.SLOT_CHECK_TIMED_OUT;
      data: { requestedDateCount: number };
    };

export const ensiteIntegrationErrorDetails: Record<EnsiteIntegrationErrorType, ErrorDetails> = {
  [EnsiteIntegrationErrorType.TEST_ERROR]: {
    sentryDescription: "Test error for verifying error handling",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [EnsiteIntegrationErrorType.WEBHOOK_HEADER_MISSING]: {
    sentryDescription: "Required Svix webhook header is missing",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [EnsiteIntegrationErrorType.WEBHOOK_SIGNATURE_INVALID]: {
    sentryDescription: "Svix webhook signature verification failed",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [EnsiteIntegrationErrorType.WEBHOOK_TIMESTAMP_STALE]: {
    sentryDescription: "Svix webhook rejected: timestamp outside the tolerance window (stale or replayed delivery)",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [EnsiteIntegrationErrorType.MISSING_GEOCODED_COORDINATES]: {
    sentryDescription: "Ensite create-survey requires geocoded coordinates, but the property address has none",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [EnsiteIntegrationErrorType.MISSING_TIMEZONE]: {
    sentryDescription: "Ensite slot lookup requires a timezone, but oddjob returned none",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [EnsiteIntegrationErrorType.MISSING_INSTALL_DATE]: {
    sentryDescription:
      "Ensite ingest needs a homeowner-selected site-visit slot or the project's estimatedStartDate to book against, but oddjob returned neither",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [EnsiteIntegrationErrorType.SLOT_LOOKUP_FAILED]: {
    sentryDescription:
      "Ensite ingest deferred to a webhook retry: oddjob's lookup of the homeowner-selected slots failed and the project has no estimatedStartDate yet",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [EnsiteIntegrationErrorType.NO_BOOKABLE_SLOT]: {
    sentryDescription:
      "Ensite ingest rejected: no enabled upcoming homeowner-selected slot and the install date is absent or past — a SITE_VISIT_BOOKING_FAILED ticket was filed for manual rebooking",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [EnsiteIntegrationErrorType.NO_SLOTS_FOR_DATE]: {
    sentryDescription: "Ensite returned no bookable slots for the Remi-specified install date",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [EnsiteIntegrationErrorType.DUPLICATE_SUBMISSION]: {
    sentryDescription: "Ensite rejected the submission as a duplicate of an existing survey",
    remiHttpCode: RemiHttpCode.CONFLICT,
  },
  [EnsiteIntegrationErrorType.SLOT_PREFILLED]: {
    sentryDescription: "Ensite rejected the create-survey request because the preferred slot is already filled",
    remiHttpCode: RemiHttpCode.CONFLICT,
  },
  [EnsiteIntegrationErrorType.AUTHENTICATION_FAILED]: {
    sentryDescription: "Ensite authentication failed: get-token endpoint returned an error or unexpected shape",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [EnsiteIntegrationErrorType.SERVER_UNAVAILABLE]: {
    sentryDescription: "Ensite get-token endpoint refused the connection (ECONNREFUSED) — server unreachable",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [EnsiteIntegrationErrorType.SLOT_CHECK_TIMED_OUT]: {
    sentryDescription: "No Ensite slot-check date answered before the deadline set under the gateway's 60s cut",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [EnsiteIntegrationErrorType.ZIP_EXTRACTION_REFUSED]: {
    sentryDescription:
      "Ensite deliverable archive breached a zip-extraction safety limit and was left unexpanded — the individual files never reached Remi",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
};
